import { ChangeDetectionStrategy, Component, computed, inject, Signal } from '@angular/core';
import { CompanyProjectItemComponent } from '@app/components/company/company-projects/company-project-item/company-project-item.component';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { ProjectsDataService } from '@app/shared/services/projects-data.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { Company } from '@app/shared/types/models/company/company.model';
import { Project } from '@app/shared/types/models/project/project.model';
import { UiSpinnerDirective } from '@app/shared/ui/spinner/ui-spinner.directive';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-project',
  templateUrl: './company-archived-projects-view.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CompanyProjectItemComponent, TranslatePipe, UiSpinnerDirective]
})
export class CompanyArchivedProjectsViewComponent {
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly projectService: ProjectsDataService = inject(ProjectsDataService);
  private readonly activeCompanyService: ActiveCompanyService = inject(ActiveCompanyService);
  private readonly dialogService: DialogService = inject(DialogService);

  protected readonly companyArchivedProjects: Signal<Project[]> = computed(() =>
    this.projectService.getCompanyArchivedProjects(this.activeCompany()?.id)()
  );
  protected activeCompany: Signal<Company | undefined> = this.activeCompanyService.activeCompany;

  public constructor() {
    if (this.activeCompany()) {
      this.projectService.loadCompanyArchivedProjects(this.activeCompany()!.id);
    }
  }

  protected openProjectTagDeleteConfirmationDialog(project: Project): void {
    this.dialogService
      .openConfirmationDialog({
        title: this.translateService.instant('company-projects.modals.delete-title'),
        desc: this.translateService.instant('company-projects.modals.delete-description', {
          name: project.name
        })
      })
      .afterClosed()
      .subscribe(decision => {
        if (decision) {
          this.deleteProject(project);
        }
      });
  }

  protected deleteProject(project: Project): void {
    this.projectService.deleteProject(project, this.activeCompany()?.id);
  }

  protected restoreProject(project: Project): void {
    this.projectService.restoreProject(project, this.activeCompany()?.id);
  }
}
